/**
 * Sensor primitives shared by REST and MQTT schemas.
 *
 * Two sensor generations coexist:
 *  - LEGACY external Wi-Fi sensor boards — standalone ESP8266 devices with
 *    their own serial numbers, paired via SoftAP and stored in
 *    `device_sensors` (see rest/devices.ts SensorConfigSchema et al.).
 *  - WIRED sensors — physically wired to the M8 controller's inputs; they
 *    talk to nothing but the controller. The controller detects them,
 *    assigns each a fixed name ("M8-S<n>"), and reports the list inside its
 *    status/state payloads (mqtt/status.ts). Their config (range, direction,
 *    auto-calibrate) rides down inside `apply_config`'s `settings.sensors`
 *    (rest/device-ops.ts StairConfigSchema).
 *
 * This file owns the primitives both generations share (type, direction,
 * hardware range bounds) plus the wired-sensor report/setting shapes.
 */
import { z } from 'zod';
/** The same sensor hardware is assigned a direction at add time: 'forward'
 *  runs the up-sweep (e.g. a sensor at the bottom of a staircase), 'backward'
 *  the down-sweep (a sensor at the top). */
export const SensorDirectionSchema = z.enum(['forward', 'backward']);
/** What is wired to a controller input — anything that can TRIGGER a run.
 *
 *  - `tof`    — VL53L1X time-of-flight. Measures an actual distance in cm, so
 *               the app can show a live readout and a walk test means something.
 *  - `pir`    — passive infrared (HC-SR501-class module on the M8 sensor PCB,
 *               firmware/m8/M8_PIR_Sensor_V1). Binary presence only: it cannot
 *               report *how far* away something is, only that something moved,
 *               and its detection span is a potentiometer on the module — not
 *               a number the wire can carry. So, like a switch, it has NO range
 *               to tune and nothing to calibrate. It does carry `motion`, but
 *               DERIVED by the controller: the node sends one trigger per
 *               motion edge and keeps no state, so the controller marks the
 *               slot occupied for a few seconds after each trigger. Unlike a
 *               switch, the controller's ambient-light gate still applies to
 *               it (a motion trigger is an inference, a press is not).
 *               Configurable: `direction` only.
 *  - `switch` — a wall switch. Not a detection sensor at all: it fires when a
 *               PERSON presses it. No distance, no range to tune, nothing to
 *               calibrate — the only thing configurable about it is which way
 *               the staircase should sweep, the same `direction` a motion
 *               sensor carries.
 *  - `ultrasonic` — HC-SR04-class ultrasonic ranging module on the ToF PCB
 *               (firmware/m8/M8_Ultrasonic_Sensor_V5). Behaves exactly like
 *               `tof` on the wire and in the app — a measured distance in cm,
 *               a live readout, a range to tune, the same auto-calibrate (5 s
 *               background sample, `average − 15 cm`) — but with a different
 *               span: the module cannot read under ~20 cm (the echo overlaps
 *               its own burst) and is rated to 4 m. Type byte 4 at pairing;
 *               the stair controller admits it from v1.5.4.
 *
 *  The distinction is not cosmetic: it decides whether a distance readout can
 *  be rendered at all, and it sets the usable range span (see
 *  [SENSOR_RANGE_BOUNDS]).
 *
 *  NOTE this is the DETECTION-input vocabulary. An ambient-light sensor is not
 *  in it — it triggers nothing, so it is reported as a slot rather than an
 *  input (mqtt/sensors.ts SensorSlotSchema, and the union on
 *  StairStatusSchema.sensors). */
export const SensorTypeSchema = z.enum(['tof', 'pir', 'switch', 'ultrasonic']);
/** Per-hardware detection-range limits and capability.
 *
 *  These are physical properties of the parts, not product decisions. The ToF
 *  board's firmware clamps to 15–150 cm (it cannot see reliably past ~1.5 m in
 *  short-distance mode); the ultrasonic board's clamps to 20–400 cm (blind
 *  under ~20 cm, rated to 4 m). Clients read the bounds from the API rather
 *  than hardcoding them, so adding a sensor type does not require an app
 *  release.
 *
 *  `maxCm: 0` is the marker for "this hardware has no range concept at all"
 *  (on the wire — the PIR module's span exists, but it is a pot on the board
 *  the installer turns, not a setting). It is a stronger statement than
 *  `reportsDistance: false`: the two are kept separate so a future presence
 *  sensor that DOES take a span over the wire can say so without a schema
 *  change. Clients render no range control when `maxCm` is 0, and
 *  [WiredSensorSettingSchema] rejects a `rangeCm` sent for such a type rather
 *  than storing a number that can never mean anything.
 *
 *  `pir` was `50..1000` until the real board shipped (M8_PIR_Sensor_V1); a
 *  `rangeCm` stored for a pir slot under that bound is dropped on re-parse.
 */
export const SENSOR_RANGE_BOUNDS = {
    tof: { minCm: 15, maxCm: 150, reportsDistance: true },
    pir: { minCm: 0, maxCm: 0, reportsDistance: false },
    switch: { minCm: 0, maxCm: 0, reportsDistance: false },
    ultrasonic: { minCm: 20, maxCm: 400, reportsDistance: true },
};
/** A controller has at most this many physical sensor inputs. */
export const WIRED_SENSOR_MAX = 8;
/**
 * One wired sensor as the CONTROLLER reports it — inside the MQTT `status`
 * payload and the LAN `GET /api/state` response, identical JSON on both legs
 * (same dual-path rule as every other status field; see mqtt/status.ts).
 *
 * - `id` is the physical input index (1..8). It is the ONLY key config refers
 *   to, and MUST be stable across reboots — never enumeration order.
 * - `name` is controller-ASSIGNED ("M8-S<n>", matching the input index) and
 *   read-only in the app; it is never sent back down.
 * - `rangeCm`/`direction` are echoes of the APPLIED config — the
 *   hardware-confirmation loop, same idea as partyEnabled/nightEnabled.
 * - `motion` is live state. The LAN /api/state MAY carry it live; an MQTT
 *   status MUST NOT be published per motion event (rate) — motion rides only
 *   the regular periodic/on-change status.
 *
 * There is deliberately NO `pattern` here. A per-sensor trigger pattern is not
 * part of the wired-sensor feature: nothing sends one down, so an echo of one
 * would be an echo of nothing. Controllers whose firmware still puts
 * `"pattern":"..."` in the report are not an error — zod strips unknown keys,
 * so the field is dropped on parse and ignored everywhere downstream.
 *
 * Everything here is the STATE tier: it rides both legs at the regular status
 * cadence. The LIVE tier (150 ms distance frames) never touches MQTT — it is
 * LAN-only, over the controller's WebSocket relay
 * ([WiredSensorLiveFrameSchema]).
 */
export const WiredSensorReportSchema = z.object({
    id: z.number().int().min(1).max(WIRED_SENSOR_MAX),
    name: z.string().min(1).max(60),
    type: SensorTypeSchema,
    rangeCm: z.number().int().optional(),
    direction: SensorDirectionSchema.optional(),
    motion: z.boolean().optional(),
    /** The LIVE lamp: is the sensor answering the controller on the wire
     *  (RS485) right now? A sensor can be listed (the input is populated) yet
     *  silent — this is what tells those apart, so the app never renders a dead
     *  board as "nothing detected". */
    connected: z.boolean().optional(),
    /** Last distance the controller read from this sensor, in cm. STATE tier —
     *  it moves at the status cadence, not per reading. The app shows it as the
     *  last known value and says so; a live number only ever comes from the LAN
     *  WebSocket relay. Present for the ranging boards (tof, ultrasonic).
     *  Absent for presence-only hardware (pir) and for a switch — as are
     *  `rangeCm`, `autoCalibrate` and `calibrating`; a pir still carries
     *  `motion` (controller-derived, see [SensorTypeSchema]). */
    distanceCm: z.number().optional(),
    /** Auto-calibrate as the controller currently has it — the echo of the
     *  configured [WiredSensorSetting.autoCalibrate]. While it is true the
     *  sensor OWNS `rangeCm`: it samples the background for 5 s at every boot,
     *  writes `average − 15 cm` to the same store the manual range uses, and the
     *  controller must NOT push a saved `rangeCm` down over it. Same rule on
     *  both ranging boards (tof, ultrasonic). */
    autoCalibrate: z.boolean().optional(),
    /** True while a background-sampling run is actually in progress (the
     *  webpage's "CALIBRATING — SAMPLING BACKGROUND…" state). */
    calibrating: z.boolean().optional(),
});
/**
 * One wired sensor's configuration as the APP sends it down — an entry in
 * `apply_config`'s `settings.sensors` array (and the stored
 * `device_config.settings.sensors`). Replace-in-full: the array is the
 * complete per-sensor config; a connected sensor whose id is absent is simply
 * unconfigured (controller default trigger behavior).
 *
 * No `name` field — names are controller-assigned, never sent down. `type` is
 * echoed from the report so range bounds can be validated server-side without
 * a live device.
 *
 * These are the ONLY fields that ever travel downward, and of them only
 * `rangeCm` + `autoCalibrate` reach the sensor board — `direction` stops at the
 * controller, which decides which way the input sweeps. Everything else on
 * [WiredSensorReportSchema] is report-only and must never be echoed back down.
 *
 * No `pattern`. A per-sensor trigger pattern was dropped from the feature: the
 * controller decides what a triggered input does, the app does not choose it.
 * The field is gone rather than deprecated-in-place, so zod strips it from any
 * config stored while it existed — an old entry re-parses without its pattern
 * and stops carrying one forward on the next save.
 */
export const WiredSensorSettingSchema = z
    .object({
    id: z.number().int().min(1).max(WIRED_SENSOR_MAX),
    type: SensorTypeSchema,
    /** The manual detection range — OPTIONAL, for two reasons.
     *
     *  A sensor can legitimately exist with nothing configured yet: the
     *  controller reports every untouched input as `"rangeCm":0`, and the user
     *  may want to turn auto-calibrate on without ever picking a number.
     *
     *  While `autoCalibrate` is on the SENSOR owns this field: it samples the
     *  background for 5 s at every boot and writes `average − 15 cm` to the
     *  same store, and the controller must not push a saved value down over
     *  that (see [autoCalibrate] below and docs/CONTRACTS.md § "Auto-calibrate
     *  owns the range while it is on"). Requiring it here contradicted that
     *  rule outright — there was no way to express "auto-calibrate is on, the
     *  sensor computes the range" without inventing a number the app is
     *  forbidden to own, and enabling auto-calibrate on a sensor that had never
     *  been given a manual range was rejected outright.
     *
     *  [WiredSensorReportSchema.rangeCm] is optional for the same reason; the
     *  two halves of the contract now agree. When present it is still bounds-
     *  checked against this sensor's own hardware below. Absent means no manual
     *  range is stored — either auto-calibrate owns it, or the input has not
     *  been configured yet. */
    rangeCm: z.number().int().optional(),
    direction: SensorDirectionSchema,
    /** Hand the detection range to the sensor's own background sampling.
     *  User-owned (it rides down like any other setting), but it INVERTS the
     *  ownership of `rangeCm`: while true the sensor recomputes the range at
     *  every boot and the controller must leave the saved `rangeCm` alone
     *  rather than overwrite a fresh calibration with a stale number. Optional
     *  so configs saved before auto-calibrate existed keep validating; absent
     *  means off. */
    autoCalibrate: z.boolean().optional(),
})
    .superRefine((s, ctx) => {
    // Only when a manual range is actually stored — an absent one is a valid
    // state (auto-calibrate owns it, or the input is unconfigured), not a
    // zero to be range-checked.
    if (s.rangeCm === undefined)
        return;
    const bounds = SENSOR_RANGE_BOUNDS[s.type];
    // Hardware with no range concept at all (a wall switch, a PIR). Rejected
    // rather than clamped or ignored: a stored number that can never be
    // applied is a lie the app would go on rendering, and the only way one
    // gets here is a bug worth surfacing.
    if (bounds.maxCm === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rangeCm'],
            message: `type '${s.type}' has no detection range; omit rangeCm`,
        });
        return;
    }
    if (s.rangeCm < bounds.minCm || s.rangeCm > bounds.maxCm) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['rangeCm'],
            message: `rangeCm must be ${bounds.minCm}..${bounds.maxCm} for type '${s.type}'`,
        });
    }
});
/**
 * One LIVE frame as the CONTROLLER relays it on its LAN WebSocket
 * (`ws://<controller-ip>:81`), ~150 ms per sensor.
 *
 * This is the sensor board's own broadcast verbatim, plus the `id` the
 * controller stamps on so a client can tell one wired sensor from another.
 * The app connects to the CONTROLLER — never to a sensor: the chain is always
 * App ↔ Controller ↔ (RS485) ↔ Sensor, and the sensor's own SoftAP webpage is
 * factory-test only.
 *
 * Deliberately LAN-only: 150 ms frames through MQTT would flood the broker,
 * and the status contract already forbids per-event status publishes. Over
 * cloud a client renders the STATE tier ([WiredSensorReportSchema]) and says
 * the reading is the last reported one — it never fabricates a live number.
 *
 * Field names are the sensor firmware's, not ours (`distance`, `max`, `auto`),
 * because the controller passes the frame through rather than re-shaping it.
 * The controller normalises the booleans the sensor sometimes writes as
 * `"1"`/`"0"` into real JSON bools.
 */
export const WiredSensorLiveFrameSchema = z.object({
    /** Which wired input this frame came from — stamped by the controller. */
    id: z.number().int().min(1).max(WIRED_SENSOR_MAX),
    /** Current reading, cm. */
    distance: z.number().optional(),
    /** OCCUPIED lamp — the sensor is tripped right now. */
    detected: z.boolean().optional(),
    /** LIVE lamp — the sensor is answering on the wire. */
    live: z.boolean().optional(),
    /** The detection range the sensor is actually holding, cm. Under
     *  auto-calibrate this is the freshly computed value, which is why the app
     *  renders it read-only instead of the saved number. */
    max: z.number().optional(),
    /** Auto-calibrate on/off as the sensor holds it. */
    auto: z.boolean().optional(),
    /** A background-sampling run is in progress (5 s). */
    calibrating: z.boolean().optional(),
});
/** Reusable refinement: wired-sensor arrays must not repeat an input id. */
export function assertUniqueWiredSensorIds(sensors, ctx) {
    const seen = new Set();
    for (const s of sensors) {
        if (seen.has(s.id)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: `duplicate wired sensor id ${s.id}`,
            });
            return;
        }
        seen.add(s.id);
    }
}
//# sourceMappingURL=sensors.js.map